import axios from 'axios';

import { apiRequest } from 'mastodon/api';
import type { ApiDirectMessageJSON } from 'mastodon/api_types/direct_messages';
import { qaLog, qaErrorDetail } from 'mastodon/utils/qa_debug';
import type { FanoutOpts } from 'mastodon/features/direct_messages/e2ee';
import type { AppDispatch } from 'mastodon/store';
// E2EE imports are lazy. The e2ee barrel transitively pulls in
// @getmaapp/signal-wasm, which uses top-level await. vite-plugin-top-level-await
// wraps every dep chunk in a Promise.all wrapper, but Rollup's chunk splitting
// hoists exported bindings into chunks whose consumers don't await __tla before
// running — surfaces as "<symbol> is not a function" on page load. This file
// is in the eager bundle (reducers/direct_messages.ts imports action types
// from here), so static-importing e2ee would contaminate the main bundle.
const loadE2EE = () => import('mastodon/features/direct_messages/e2ee');

// initial_state.ts touches `document` at import time, which breaks node-env
// tests that don't mock it — read `me` lazily, only when a send actually
// needs to split sibling-device copies into a self-addressed request.
const loadMyAccountId = async (): Promise<string | null> => {
  const { me } = await import('mastodon/initial_state');
  return me ? String(me) : null;
};

// Recognise the specific 404 the server returns when the recipient (or
// any other account in the fanout) has no E2EE identity registered —
// OR the typed client-side error thrown when the peer has no devices to
// encrypt to at all (encryptMultiDevice). Surfaces as a typed flag on the
// rejected promise so the UI layer doesn't have to parse axios error guts.
export function isMissingRecipientIdentity(err: unknown): boolean {
  if (
    err instanceof Error &&
    (err as Error & { code?: string }).code === 'e2ee_recipient_no_devices'
  ) {
    return true;
  }
  if (!axios.isAxiosError(err)) return false;
  const status = err.response?.status;
  const body = err.response?.data as { error?: string } | undefined;
  return status === 404 && body?.error === 'No E2EE identity';
}

// Recognise the typed 422 returned when the recipient's premium DM privacy
// setting rejects the sender (only mutuals and premium members may message
// them). Returns the server's human-readable reason, or null when the error
// is something else.
export function isDmRestricted(err: unknown): string | null {
  if (!axios.isAxiosError(err)) return null;
  const body = err.response?.data as
    | { error?: string; code?: string }
    | undefined;
  return err.response?.status === 422 && body?.code === 'dm_privacy'
    ? (body.error ?? 'This member has restricted who can message them.')
    : null;
}

// Recognise the typed error thrown by establishSession when the recipient's
// Kyber (post-quantum) prekeys are exhausted on the server. Passing empty
// Uint8Array to process_pre_key_bundle causes an opaque "Operation failed";
// this check lets the UI surface a clear, actionable message instead.
export function isKyberPrekeysExhausted(err: unknown): boolean {
  return (
    err instanceof Error &&
    (err as Error & { code?: string }).code === 'kyber_prekeys_depleted'
  );
}

function arrayBufferToBase64(buf: ArrayBuffer): string {
  const u8 = new Uint8Array(buf);
  // Chunk to avoid call-stack blowup on large buffers (String.fromCharCode
  // spread).
  let s = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < u8.length; i += CHUNK) {
    s += String.fromCharCode.apply(
      null,
      u8.subarray(i, i + CHUNK) as unknown as number[],
    );
  }
  return btoa(s);
}

export const DIRECT_MESSAGES_FETCH_REQUEST = 'DIRECT_MESSAGES_FETCH_REQUEST';
export const DIRECT_MESSAGES_FETCH_SUCCESS = 'DIRECT_MESSAGES_FETCH_SUCCESS';
export const DIRECT_MESSAGES_FETCH_FAIL = 'DIRECT_MESSAGES_FETCH_FAIL';

export const DIRECT_MESSAGES_THREAD_REQUEST = 'DIRECT_MESSAGES_THREAD_REQUEST';
export const DIRECT_MESSAGES_THREAD_SUCCESS = 'DIRECT_MESSAGES_THREAD_SUCCESS';
export const DIRECT_MESSAGES_THREAD_FAIL = 'DIRECT_MESSAGES_THREAD_FAIL';

export const DIRECT_MESSAGES_SEND_REQUEST = 'DIRECT_MESSAGES_SEND_REQUEST';
export const DIRECT_MESSAGES_SEND_SUCCESS = 'DIRECT_MESSAGES_SEND_SUCCESS';
export const DIRECT_MESSAGES_SEND_FAIL = 'DIRECT_MESSAGES_SEND_FAIL';

export const DIRECT_MESSAGES_DELETE_REQUEST = 'DIRECT_MESSAGES_DELETE_REQUEST';
export const DIRECT_MESSAGES_DELETE_SUCCESS = 'DIRECT_MESSAGES_DELETE_SUCCESS';
export const DIRECT_MESSAGES_DELETE_FAIL = 'DIRECT_MESSAGES_DELETE_FAIL';

export const DIRECT_MESSAGES_THREAD_NEW = 'DIRECT_MESSAGES_THREAD_NEW';

export const DIRECT_MESSAGES_THREAD_CLEAR = 'DIRECT_MESSAGES_THREAD_CLEAR';

export const DIRECT_MESSAGES_UNREAD_COUNT_SUCCESS =
  'DIRECT_MESSAGES_UNREAD_COUNT_SUCCESS';

export const DM_READ_RECEIPT = 'DM_READ_RECEIPT';

export const receiveDmReadReceipt = (payload: {
  reader_account_id: string;
  read_at: string;
  message_ids: string[];
}) => ({ type: DM_READ_RECEIPT, payload });

export const receiveDmDeleted = (payload: { dm_id: string }) => ({
  type: DIRECT_MESSAGES_DELETE_SUCCESS,
  id: payload.dm_id,
});

// Fetch inbox (one latest message per conversation)
export const fetchDirectMessages = () => async (dispatch: AppDispatch) => {
  dispatch({ type: DIRECT_MESSAGES_FETCH_REQUEST });
  try {
    const data = await apiRequest<ApiDirectMessageJSON[]>(
      'GET',
      'v1/direct_messages',
    );
    dispatch({ type: DIRECT_MESSAGES_FETCH_SUCCESS, messages: data });
  } catch (err) {
    dispatch({ type: DIRECT_MESSAGES_FETCH_FAIL, error: err });
  }
};

// Fetch thread with a specific account
export const fetchThread =
  (accountId: string, maxId?: string) => async (dispatch: AppDispatch) => {
    // maxId must ride the REQUEST: without it the reducer blanks the loaded
    // window for the duration of an older-page (scroll-up) fetch.
    dispatch({ type: DIRECT_MESSAGES_THREAD_REQUEST, accountId, maxId });
    const loadStartedAt = Date.now();
    try {
      const { getMyDeviceId } = await loadE2EE();
      const deviceId = await getMyDeviceId();
      const params: Record<string, string> = { account_id: accountId };
      if (maxId) params.max_id = maxId;
      if (deviceId !== undefined) params.device_id = String(deviceId);
      const data = await apiRequest<ApiDirectMessageJSON[]>(
        'GET',
        'v1/direct_messages/thread',
        {
          params,
        },
      );
      qaLog(
        'thread_load',
        `thread with ${accountId}: ${data.length} rows in ${Date.now() - loadStartedAt}ms${maxId ? ' (older page)' : ''}`,
      );
      dispatch({
        type: DIRECT_MESSAGES_THREAD_SUCCESS,
        accountId,
        messages: data,
        maxId,
      });
      // MD-h: opening a thread is a good moment to re-deliver any copies our own
      // siblings missed — the sibling may have come online since the send.
      // Fire-and-forget; only the initial page load (no maxId) triggers it so
      // scroll-back pagination doesn't repeatedly re-flush.
      if (!maxId) void dispatch(flushSiblingBackfill());
    } catch (err) {
      dispatch({ type: DIRECT_MESSAGES_THREAD_FAIL, accountId, error: err });
    }
  };

// Poll for new messages newer than a given id. Used for realtime updates.
// Only dispatches if there are new messages.
export const pollDirectMessageThread =
  (accountId: string, sinceId?: string) => async (dispatch: AppDispatch) => {
    try {
      const { getMyDeviceId, scheduleAutoBackup } = await loadE2EE();
      const deviceId = await getMyDeviceId();
      const params: Record<string, string> = { account_id: accountId };
      if (sinceId) params.since_id = sinceId;
      if (deviceId !== undefined) params.device_id = String(deviceId);
      const data = await apiRequest<ApiDirectMessageJSON[]>(
        'GET',
        'v1/direct_messages/thread',
        { params },
      );
      if (data.length > 0) {
        dispatch({
          type: DIRECT_MESSAGES_THREAD_NEW,
          accountId,
          messages: data,
        });
        // New inbound messages advance our Signal session state — upload a
        // fresh backup (debounced) so another device can pick up from here.
        scheduleAutoBackup();
      }
      return data;
    } catch {
      return [];
    }
  };

// Poll the protocol endpoint (protocol/content split): control-plane rows
// (acks / resend requests+answers / backfill / sibling-resend) for this
// device, ascending since the cursor. Returns the RAW rows — they must never
// enter threadMessages; the caller (the thread pane's central protocol
// processor) decrypts and dispatches their effects itself.
export const pollProtocolRows =
  (accountId: string, sinceId?: string) =>
  async (): Promise<ApiDirectMessageJSON[]> => {
    try {
      const { getMyDeviceId } = await loadE2EE();
      const deviceId = await getMyDeviceId();
      const params: Record<string, string> = { account_id: accountId };
      if (sinceId) params.since_id = sinceId;
      if (deviceId !== undefined) params.device_id = String(deviceId);
      return await apiRequest<ApiDirectMessageJSON[]>(
        'GET',
        'v1/direct_messages/protocol',
        { params },
      );
    } catch {
      return [];
    }
  };

// Media staged by the composer for an E2EE send: local bytes, never an
// uploaded media_attachment id (H-2 — plaintext must never be uploaded).
export interface ComposedMedia {
  blob: Blob;
}

// Send an E2EE message. Flow:
//   1. If the compose box staged a media file, read its LOCAL bytes, AES-GCM
//      encrypt them client-side, then include the ciphertext as base64 in the
//      request body (stored in direct_messages.encrypted_media bytea). The
//      plaintext is NEVER uploaded to /api/v2/media (H-2). The AES key + IV
//      are carried inside the Signal-encrypted payload so the server never
//      sees them.
//   2. Encrypt { text, mediaKey, mediaIv } via Signal protocol.
//   3. POST ciphertext + type + (optional) encrypted_media. Sibling-device
//      copies (fanout entries addressed to our OWN account) go in a SEPARATE
//      request addressed to ourselves — the server rejects per-target
//      recipient overrides (L-22).
export const sendDirectMessage =
  (
    recipientAccountId: string,
    text: string,
    media?: ComposedMedia,
    replyInfo?: {
      replyToId: string;
      senderId: string;
      senderName?: string;
      snippet: string;
    },
    forwarded?: boolean,
    resendOfId?: string,
    forceReestablish?: boolean,
    // Slim-fanout options for PROTOCOL sends (resend-storm fix). Content
    // sends leave this undefined — full fanout is correct for real messages.
    fanoutOpts?: FanoutOpts,
    // Resend-answer of a MEDIA message: the AES key/IV recovered from the
    // sent-plaintext cache. The encrypted bytes already sit on the original
    // row server-side, so only the key material is re-delivered inside the
    // Signal payload — no new encrypted_media blob is posted.
    resendMedia?: { mediaKey?: string; mediaIv?: string },
    // Protocol/content split: mark every fanout row of this logical message
    // as control-plane protocol traffic. Protocol rows are excluded from the
    // content thread window / unread count / inbox preview and served via
    // GET v1/direct_messages/protocol. Content sends leave this undefined.
    protocol?: boolean,
  ) =>
  async (dispatch: AppDispatch) => {
    dispatch({ type: DIRECT_MESSAGES_SEND_REQUEST });
    try {
      const {
        checkKeyMaterialHealth,
        encryptAttachmentBytes,
        fanoutEncrypt,
        getMyDeviceId,
        enqueueSiblingBackfill,
        rememberSent,
        scheduleAutoBackup,
        stampSiblingBackfillGroupKey,
      } = await loadE2EE();
      // Detect mid-session IDB prekey loss before encrypting; debounced so
      // only runs at most once per 30 minutes per page session.
      await checkKeyMaterialHealth();
      let encryptedMediaB64: string | undefined;
      let mediaKey: string | undefined;
      let mediaIv: string | undefined;

      if (media) {
        // H-2: read the local File/Blob bytes directly — no /api/v2/media
        // upload, no plaintext download-back.
        const plainBytes = await media.blob.arrayBuffer();
        const enc = await encryptAttachmentBytes(plainBytes);
        encryptedMediaB64 = arrayBufferToBase64(enc.ciphertext);
        mediaKey = enc.key;
        mediaIv = enc.iv;
      } else if (resendMedia) {
        // Resend answer: re-deliver the ORIGINAL key material only. The
        // recipient fetches the encrypted bytes from the original row's
        // /media endpoint — re-posting a blob would duplicate ciphertext.
        mediaKey = resendMedia.mediaKey;
        mediaIv = resendMedia.mediaIv;
      }

      const payload = {
        text,
        mediaKey,
        mediaIv,
        ...(replyInfo && {
          reply_to: {
            message_id: replyInfo.replyToId,
            sender_id: replyInfo.senderId,
            sender_name: replyInfo.senderName,
            snippet: replyInfo.snippet,
          },
        }),
        ...(resendOfId && { resend_of_id: resendOfId }),
      };
      const fanout = await fanoutEncrypt(
        recipientAccountId,
        payload,
        forceReestablish,
        fanoutOpts,
      );
      const senderDeviceId = await getMyDeviceId();

      const myAccountId = await loadMyAccountId();
      // Split the fanout: targets for THE RECIPIENT go in the recipient's
      // request; copies for our OWN sibling devices go in a separate request
      // addressed to our own account (the server 422s a per-target
      // recipient_account_id that doesn't match the request recipient).
      const peerTargets = fanout.filter(
        (f) => f.recipient_account_id !== myAccountId,
      );
      const selfTargets = fanout.filter(
        (f) => f.recipient_account_id === myAccountId,
      );

      const buildBody = (
        addressedTo: string,
        targets: typeof fanout,
        groupKey?: string,
      ): Record<string, unknown> => {
        const body: Record<string, unknown> = {
          recipient_account_id: addressedTo,
          sender_device_id: senderDeviceId ?? 1,
          targets: targets.map((f) => ({
            recipient_account_id: f.recipient_account_id,
            recipient_device_id: f.recipient_device_id,
            ciphertext: f.ciphertextB64,
            ciphertext_type: f.type,
          })),
        };
        // Sibling copies must share the primary send's group_key or they
        // thread as separate messages (the server validates the key against
        // the existing thread and 422s an unknown one).
        if (groupKey) body.group_key = groupKey;
        // The encrypted blob rides with every copy so each device can fetch
        // and decrypt it from its own row.
        if (encryptedMediaB64) body.encrypted_media = encryptedMediaB64;
        if (replyInfo) body.reply_to_id = replyInfo.replyToId;
        if (forwarded) body.forwarded = true;
        // Protocol sends mark EVERY fanout row (peer targets AND the
        // self-addressed sibling request) so each device's copy is classified
        // identically server-side.
        if (protocol) body.protocol = true;
        return body;
      };

      // SUCCESS = the RECIPIENT's copy landed. Everything after the peer POST
      // is best-effort: a failed sibling-copy POST or local cache write must
      // never mark a DELIVERED message as failed (operator report 2026-08-08:
      // "failed to send" and "seen" on the same message — the recipient had
      // read a message the sender's client claimed never went out).
      let data: ApiDirectMessageJSON | undefined;
      let selfDelivered = false;
      if (peerTargets.length > 0) {
        data = await apiRequest<ApiDirectMessageJSON>(
          'POST',
          'v1/direct_messages',
          { data: buildBody(recipientAccountId, peerTargets) },
        );
      } else if (selfTargets.length > 0 && myAccountId) {
        // Self-thread (no peer devices): the self POST IS the delivery and
        // stays on the critical path.
        data = await apiRequest<ApiDirectMessageJSON>(
          'POST',
          'v1/direct_messages',
          { data: buildBody(myAccountId, selfTargets) },
        );
        selfDelivered = true;
      }
      if (!data) {
        throw new Error('e2ee_recipient_no_devices');
      }
      const delivered = data;

      try {
        // Cache the plaintext so our own thread can render it. Key by
        // group_key — the server creates multiple fanout rows per logical
        // message, and the one that ends up in our thread after dedupe
        // isn't necessarily the row whose id came back here.
        await rememberSent(delivered.group_key ?? delivered.id, payload);
        // MD-h repair: the fan-out may have queued sibling-backfill copies
        // BEFORE this POST returned, so they couldn't know their logical id.
        // Stamp them now — the next flush threads each copy onto this message
        // instead of minting an orphan protocol row.
        if (delivered.group_key) {
          await stampSiblingBackfillGroupKey(
            recipientAccountId,
            delivered.group_key,
          );
        }
      } catch (cacheErr) {
        // Worst case the sender's own bubble renders as unavailable — the
        // message is delivered regardless.
        console.warn('[DM] sent-cache write failed after delivery:', cacheErr);
      }

      dispatch({ type: DIRECT_MESSAGES_SEND_SUCCESS, message: delivered });
      // Debounced auto-backup so the new sent-plaintext cache entry +
      // any session advance lands on the server. Silent no-op if the
      // user hasn't set up a backup yet.
      scheduleAutoBackup();

      // Copies for our OWN other devices ride AFTER success, off the critical
      // path: a failure here queues a durable MD-h backfill instead of lying
      // about the delivered send. Fire-and-forget — never delays the bubble
      // or the composer.
      void (async () => {
        if (selfTargets.length > 0 && !selfDelivered && myAccountId) {
          try {
            await apiRequest<ApiDirectMessageJSON>(
              'POST',
              'v1/direct_messages',
              {
                data: buildBody(
                  myAccountId,
                  selfTargets,
                  delivered.group_key ?? undefined,
                ),
              },
            );
          } catch (siblingErr) {
            console.warn(
              '[DM] sibling-copy POST failed after delivery — queuing backfill:',
              siblingErr,
            );
            try {
              await enqueueSiblingBackfill(
                recipientAccountId,
                payload,
                selfTargets.map((t) => t.recipient_device_id),
              );
            } catch {
              /* best-effort — the periodic MD-h sweep remains the backstop */
            }
          }
        }
        // MD-h: a successful send means our own siblings are likely reachable
        // now — opportunistically re-deliver any copies they missed earlier.
        void dispatch(flushSiblingBackfill());
      })();
    } catch (err) {
      qaLog(
        'dm_send_fail',
        `send to ${recipientAccountId} failed`,
        qaErrorDetail(err),
      );
      // Background control-plane pings (gap reasserts, sibling resend
      // requests) fail silently — the errors middleware must not raise an
      // "unexpected error" toast for traffic the user never initiated.
      dispatch({
        type: DIRECT_MESSAGES_SEND_FAIL,
        error: err,
        skipAlert: protocol === true,
      });
      throw err;
    }
  };

// MD-h: re-deliver message copies our own sibling devices missed at send time
// (their Signal session was throttled/unestablishable, so the original fan-out
// skipped them and durably queued a backfill). Best-effort, fire-and-forget:
// any failure is swallowed and the entry stays queued for the next attempt. The
// drain is two-phase — the queue entry is removed only AFTER its re-delivery
// POST succeeds (confirmSiblingBackfill), so a failed POST can't lose the copy.
export const flushSiblingBackfill = () => async () => {
  try {
    const { flushSiblingBackfills, confirmSiblingBackfill, getMyDeviceId } =
      await loadE2EE();
    const flushed = await flushSiblingBackfills();
    if (flushed.length === 0) return;
    const senderDeviceId = (await getMyDeviceId()) ?? 1;
    const myAccountId = await loadMyAccountId();
    if (!myAccountId) return;
    // Group by thread AND logical message: entries from different sends can
    // share a thread but carry different stamped group_keys, and one POST
    // carries one group_key.
    const byGroup = new Map<
      string,
      { threadAccountId: string; groupKey?: string; entries: typeof flushed }
    >();
    for (const f of flushed) {
      const key = `${f.threadAccountId} ${f.groupKey ?? ''}`;
      const group = byGroup.get(key) ?? {
        threadAccountId: f.threadAccountId,
        groupKey: f.groupKey,
        entries: [],
      };
      group.entries.push(f);
      byGroup.set(key, group);
    }
    for (const { threadAccountId, groupKey, entries } of byGroup.values()) {
      try {
        // Sibling copies are addressed to OUR OWN account — the server
        // rejects per-target recipient overrides (L-22).
        await apiRequest('POST', 'v1/direct_messages', {
          data: {
            recipient_account_id: myAccountId,
            sender_device_id: senderDeviceId,
            // Protocol/content split: sibling re-delivery is control-plane
            // recovery traffic, NOT a new logical message. Unflagged, each
            // flushed copy landed as a fresh content row (the server mints a
            // new group_key per call) — the "extra logical DM ~1s after a
            // real send" phantom (2026-07-31 forensics).
            protocol: true,
            // MD-h repair: the group_key stamped after the primary send's
            // POST threads this copy onto the ORIGINAL logical message — the
            // receiving sibling's thread window admits the row by group_key
            // and renders the message's bubble. Absent for pre-repair queue
            // entries: the server mints a fresh key and the copy stays
            // protocol-plane-only, exactly as before.
            ...(groupKey ? { group_key: groupKey } : {}),
            targets: entries.map((f) => ({
              recipient_account_id: myAccountId,
              recipient_device_id: f.recipient_device_id,
              ciphertext: f.ciphertextB64,
              ciphertext_type: f.type,
            })),
          },
        });
        // POST landed — drain each delivered copy (phase 2).
        for (const f of entries) {
          await confirmSiblingBackfill(threadAccountId, f.recipient_device_id);
        }
      } catch {
        // Leave the entries queued; retried on the next flush.
      }
    }
  } catch {
    // best-effort background recovery
  }
};

// Delete a message
export const deleteDirectMessage =
  (id: string) => async (dispatch: AppDispatch) => {
    dispatch({ type: DIRECT_MESSAGES_DELETE_REQUEST, id });
    try {
      await apiRequest('DELETE', `v1/direct_messages/${id}`);
      dispatch({ type: DIRECT_MESSAGES_DELETE_SUCCESS, id });
    } catch (err) {
      dispatch({ type: DIRECT_MESSAGES_DELETE_FAIL, id, error: err });
    }
  };

export const clearThread = () => ({ type: DIRECT_MESSAGES_THREAD_CLEAR });

export const CONVERSATION_DELETE_SUCCESS = 'CONVERSATION_DELETE_SUCCESS';

export const receiveConversationDeleted = (
  payload: { initiator_account_id: string; other_account_id: string },
  currentAccountId: string,
) => ({
  type: CONVERSATION_DELETE_SUCCESS,
  // The "other" account from the current user's perspective:
  otherAccountId:
    payload.initiator_account_id === currentAccountId
      ? payload.other_account_id
      : payload.initiator_account_id,
});

export const deleteConversation =
  (accountId: string) => async (dispatch: AppDispatch) => {
    dispatch({
      type: CONVERSATION_DELETE_SUCCESS,
      otherAccountId: accountId,
    });
    try {
      await apiRequest('DELETE', 'v1/direct_messages/conversation', {
        params: { account_id: accountId },
      });
    } catch (err) {
      console.error('[DM] deleteConversation failed:', err);
      throw err;
    }
  };

export const DM_REACTION_SUCCESS = 'DM_REACTION_SUCCESS';
export const DM_REACTION_UPDATE = 'DM_REACTION_UPDATE';

export const toggleDmReaction =
  (messageId: string, emoji: string, me: boolean) =>
  async (dispatch: AppDispatch) => {
    try {
      const data = await apiRequest<ApiDirectMessageJSON>(
        me ? 'DELETE' : 'POST',
        me
          ? `v1/direct_messages/${messageId}/reactions/${encodeURIComponent(emoji)}`
          : `v1/direct_messages/${messageId}/reactions`,
        me ? undefined : { data: { emoji } },
      );
      dispatch({ type: DM_REACTION_SUCCESS, message: data });
    } catch (err) {
      console.error('[DM reaction] failed:', err);
    }
  };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const receiveDmReaction = (
  payload: any,
  currentAccountId: string,
): any => ({
  type: DM_REACTION_UPDATE,
  payload: { ...payload, currentAccountId },
});

export const DIRECT_MESSAGES_EDIT_SUCCESS = 'DIRECT_MESSAGES_EDIT_SUCCESS';
export const DM_EDITED = 'DM_EDITED';

export const editDirectMessage =
  (
    messageId: string,
    groupKey: string,
    recipientAccountId: string,
    newText: string,
  ) =>
  async (dispatch: AppDispatch) => {
    try {
      const { fanoutEncrypt, getMyDeviceId, rememberSent } = await loadE2EE();
      const payload = { text: newText };
      const fanout = await fanoutEncrypt(recipientAccountId, payload);
      const senderDeviceId = await getMyDeviceId();

      const body: Record<string, unknown> = {
        sender_device_id: senderDeviceId ?? 1,
        targets: fanout.map((f) => ({
          recipient_account_id: f.recipient_account_id,
          recipient_device_id: f.recipient_device_id,
          ciphertext: f.ciphertextB64,
          ciphertext_type: f.type,
        })),
      };

      const data = await apiRequest<ApiDirectMessageJSON>(
        'PATCH',
        `v1/direct_messages/${messageId}`,
        { data: body },
      );

      await rememberSent(groupKey, payload).catch(() => {});
      await rememberSent(messageId, payload).catch(() => {});

      dispatch({
        type: DIRECT_MESSAGES_EDIT_SUCCESS,
        message: data,
        text: newText,
      });
    } catch (err) {
      throw err;
    }
  };

export const receiveDmEdited = (payload: {
  message_id: string;
  group_key: string;
  sender_account_id: string;
  recipient_account_id: string;
  edited_at: string;
}) => ({
  type: DM_EDITED,
  messageId: payload.message_id,
  editedAt: payload.edited_at,
});

export const handleDmEdited =
  (payload: {
    message_id: string;
    group_key: string;
    sender_account_id: string;
    recipient_account_id: string;
    edited_at: string;
  }) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async (dispatch: AppDispatch, getState: any) => {
    import('mastodon/initial_state')
      .then(({ me }) => {
        const currentAccountId = me ? String(me) : '';
        const isSender = payload.sender_account_id === currentAccountId;

        dispatch({
          type: DM_EDITED,
          messageId: payload.message_id,
          editedAt: payload.edited_at,
          groupKey: payload.group_key,
        });

        if (!isSender) {
          const state = getState();
          const threadAccountId = state.direct_messages?.threadAccountId as
            | string
            | null;
          const otherAccountId = payload.sender_account_id;
          if (threadAccountId === otherAccountId) {
            void dispatch(fetchThread(otherAccountId));
          }
        }
      })
      .catch(() => {});
  };

export const DM_TYPING = 'DM_TYPING';
export const DM_TYPING_CLEAR = 'DM_TYPING_CLEAR';

export const receiveDmTyping = (payload: { sender_account_id: string }) => ({
  type: DM_TYPING,
  accountId: payload.sender_account_id,
});

export const clearDmTyping = () => ({ type: DM_TYPING_CLEAR });

// Fetch unread DM count
export const fetchUnreadDmCount = () => async (dispatch: AppDispatch) => {
  try {
    const data = await apiRequest<{ count: number }>(
      'GET',
      'v1/direct_messages/unread_count',
    );
    dispatch({ type: DIRECT_MESSAGES_UNREAD_COUNT_SUCCESS, count: data.count });
  } catch {
    // silently fail
  }
};
